// In-progress défi persistence — lets the user resume a défi after a
// page reload (via the "Reprendre ?" modal) instead of silently losing
// their progress on refresh.
//
// Scope: only the *session-transient* state needed to resume — counters,
// seen cardIds, and the running errors list. The defi.total bound is
// re-read from GAME_MODES at resume time so changes to the total stay
// consistent across reloads.
//
// Not persisted: the "done" flag (we only save an *in-progress* défi —
// completed défis clear their entry), and the gameMode itself (already
// persisted separately under kartichki.gameMode).

import { STORAGE_KEYS } from "../config/storage-keys";
import type { DefiError } from "../types";

/** Serialized shape of an in-progress défi. `seenIds` is an array here
 *  (Sets don't JSON-serialize cleanly) — the controller rehydrates to a
 *  Set at load time. */
export type PersistedDefi = {
  /** Which deck the défi was running on. Resume is skipped if the user
   *  switched decks between reloads — the card pool would mismatch. */
  listId: string;
  total: number;
  errors: DefiError[];
  seenIds: string[];
  /** Running session counters at save time — so the resume prompt can
   *  display "23 / 50" and the restored défi counts continue from the
   *  right number. */
  attempts: number;
  correct: number;
  /** Unix millis. Used for staleness checks by the caller (e.g. auto-
   *  abandon after N hours) and for UI display if we ever want to show
   *  "started 3h ago". */
  startedAt: number;
};

export function loadDefiState(
  ls: Storage = localStorage,
): PersistedDefi | null {
  try {
    const raw = ls.getItem(STORAGE_KEYS.DEFI);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    // Light validation — any missing primitive gets a safe default so a
    // partially-corrupted blob doesn't explode the boot path. If the
    // shape is too broken to be usable the caller can discard.
    const listId = typeof parsed.listId === "string" ? parsed.listId : "";
    const total = Number.isFinite(parsed.total) ? Number(parsed.total) : 0;
    const attempts = Number.isFinite(parsed.attempts) ? Number(parsed.attempts) : 0;
    const correct = Number.isFinite(parsed.correct) ? Number(parsed.correct) : 0;
    const startedAt = Number.isFinite(parsed.startedAt) ? Number(parsed.startedAt) : 0;
    const errors: DefiError[] = Array.isArray(parsed.errors)
      ? parsed.errors
          .filter(
            (e: unknown): e is DefiError =>
              !!e &&
              typeof e === "object" &&
              typeof (e as DefiError).cardId === "string" &&
              typeof (e as DefiError).prompt === "string" &&
              typeof (e as DefiError).expected === "string",
          )
          // Carry `userAnswer` through if present — it's optional on
          // SessionError. Defensive: only accept a string; anything
          // else gets dropped so a corrupt blob can't poison the recap.
          .map((e: DefiError) => {
            const ua = (e as { userAnswer?: unknown }).userAnswer;
            return typeof ua === "string"
              ? { ...e, userAnswer: ua }
              : {
                  cardId: e.cardId,
                  prompt: e.prompt,
                  expected: e.expected,
                };
          })
      : [];
    const seenIds: string[] = Array.isArray(parsed.seenIds)
      ? parsed.seenIds.filter((x: unknown): x is string => typeof x === "string")
      : [];
    if (!listId || total <= 0) return null;
    return { listId, total, errors, seenIds, attempts, correct, startedAt };
  } catch {
    return null;
  }
}

export function saveDefiState(
  state: PersistedDefi,
  ls: Storage = localStorage,
): void {
  ls.setItem(STORAGE_KEYS.DEFI, JSON.stringify(state));
}

export function clearDefiState(ls: Storage = localStorage): void {
  ls.removeItem(STORAGE_KEYS.DEFI);
}
